/**
 * Cart Widget JavaScript
 * Uses WooCommerce Store API for cart updates - NO PAGE REFRESH
 * jQuery-free vanilla JavaScript implementation
 *
 * @package Storedash
 * @since 3.2.0
 */

(function() {
    'use strict';

    // Configuration from WordPress with defensive fallbacks
    const config = window.storedashCartWidgetConfig || {};
    config.storeApiUrl = config.storeApiUrl || '/wp-json/wc/store/v1/';
    config.storeApiNonce = config.storeApiNonce || '';
    config.i18n = config.i18n || {};

    /**
     * Persist cart data to shared localStorage for mini-cart cross-navigation cache.
     * Called after any cart mutation (update-item, remove-item) so the mini-cart
     * badge and sidebar render instantly on the next page load.
     *
     * @param {Object} cart - Normalized cart data from Store API response
     */
    function syncCartToLocalStorage(cart) {
        // Defer to next microtask so the browser has applied the Set-Cookie header
        // from the Store API response (woocommerce_cart_hash cookie update).
        setTimeout(() => {
            try {
                const hashMatch = document.cookie.match(/(?:^|;\s*)woocommerce_cart_hash=([^;]*)/);
                const cartHash = hashMatch ? decodeURIComponent(hashMatch[1]) : '';
                if (!cartHash) return;
                const count = cart.items_count || 0;
                localStorage.setItem('storedash_cart_cache', JSON.stringify({
                    hash: cartHash,
                    ts: Date.now(),
                    cart: cart
                }));
                if (count > 0) {
                    localStorage.setItem('storedash_cart_count', String(count));
                    localStorage.setItem('storedash_cart_hash', cartHash);
                } else {
                    localStorage.removeItem('storedash_cart_count');
                    localStorage.removeItem('storedash_cart_hash');
                    localStorage.removeItem('storedash_cart_cache');
                }
            } catch (e) { /* localStorage unavailable */ }
        }, 50);
    }

    /**
     * Store API Client for Cart Operations
     */
    class StoreAPIClient {
        constructor(baseUrl, nonce) {
            this.baseUrl = baseUrl.replace(/\/$/, '');
            this.nonce = nonce;
            this.maxRetries = 3;
            this.retryDelay = 500;
        }

        /**
         * Update item quantity in cart
         */
        async updateItem(itemKey, quantity) {
            return this.request('POST', '/cart/update-item', {
                key: itemKey,
                quantity: parseInt(quantity, 10)
            });
        }

        /**
         * Remove item from cart
         */
        async removeItem(itemKey) {
            return this.request('POST', `/cart/remove-item?key=${itemKey}`);
        }

        /**
         * Get current cart data
         */
        async getCart() {
            // Add timestamp to prevent CDN/proxy caching (LiteSpeed, WP Rocket, Cloudflare)
            return this.request('GET', `/cart?_=${Date.now()}`);
        }

        /**
         * Make Store API request with retry logic
         */
        async request(method, endpoint, body = null, retryCount = 0) {
            const url = `${this.baseUrl}${endpoint}`;

            const options = {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Nonce': this.nonce,
                    // Cart data is user-specific - never cache (LiteSpeed, WP Rocket, Cloudflare)
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                },
                credentials: 'same-origin'
            };

            if (body && (method === 'POST' || method === 'PUT')) {
                options.body = JSON.stringify(body);
            }

            try {
                const response = await fetch(url, options);

                // Handle authentication errors
                if (response.status === 401 || response.status === 403) {
                    if (retryCount === 0) {
                        const refreshed = await this.refreshNonce();
                        if (refreshed) {
                            return this.request(method, endpoint, body, retryCount + 1);
                        }
                    }
                    throw new Error('Authentication failed. Please refresh the page.');
                }

                // Handle rate limiting
                if (response.status === 429) {
                    if (retryCount < this.maxRetries) {
                        const retryAfter = parseInt(response.headers.get('Retry-After')) || 2;
                        await this.sleep(retryAfter * 1000);
                        return this.request(method, endpoint, body, retryCount + 1);
                    }
                    throw new Error('Too many requests. Please try again in a moment.');
                }

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.message || `HTTP ${response.status}`);
                }

                return await response.json();

            } catch (error) {
                // Retry on network errors
                if (retryCount < this.maxRetries && this.isRetryableError(error)) {
                    const delay = this.retryDelay * Math.pow(2, retryCount);
                    await this.sleep(delay);
                    return this.request(method, endpoint, body, retryCount + 1);
                }
                throw error;
            }
        }

        isRetryableError(error) {
            return error.message.includes('Failed to fetch') ||
                   error.message.includes('NetworkError') ||
                   error.message.includes('HTTP 5');
        }

        async refreshNonce() {
            try {
                const response = await fetch('/wp-json/wc/store/v1/cart', {
                    method: 'GET',
                    credentials: 'same-origin',
                    headers: { 'Cache-Control': 'no-cache' }
                });

                const newNonce = response.headers.get('Nonce') || response.headers.get('X-WC-Store-API-Nonce');
                if (newNonce && newNonce !== this.nonce) {
                    this.nonce = newNonce;
                    config.storeApiNonce = newNonce;
                    return true;
                }
                return false;
            } catch (error) {
                return false;
            }
        }

        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }
    }

    // Initialize Store API client
    const storeAPI = new StoreAPIClient(config.storeApiUrl, config.storeApiNonce);

    /**
     * Cart Widget Handler
     */
    class StoredashCartWidget {
        constructor(element) {
            this.element = element;
            this.form = element.querySelector('.woocommerce-cart-form');
            this.updateTimeout = null;
            this.isUpdating = false;

            this.init();
        }

        init() {
            this.bindEvents();
        }

        /**
         * Bind events using event delegation
         */
        bindEvents() {
            // Quantity +/- buttons
            this.element.addEventListener('click', (e) => {
                if (e.target.matches('.quantity-minus, .quantity-plus')) {
                    this.handleQuantityButtons(e);
                }
                if (e.target.matches('.remove-item')) {
                    this.handleRemoveItem(e);
                }
                if (e.target.matches('.update-cart')) {
                    this.handleUpdateCart(e);
                }
            });

            // Quantity input change (debounced)
            this.element.addEventListener('change', (e) => {
                if (e.target.matches('input.qty')) {
                    this.handleQuantityChange(e);
                }
            });
        }

        /**
         * Handle quantity +/- buttons
         */
        handleQuantityButtons(e) {
            e.preventDefault();

            const button = e.target.closest('.quantity-minus, .quantity-plus');
            if (!button) return;

            const wrapper = button.parentElement;
            const input = wrapper.querySelector('input.qty');
            if (!input) return;

            const currentVal = parseInt(input.value) || 1;
            const min = parseInt(input.getAttribute('min')) || 0;
            const max = parseInt(input.getAttribute('max')) || 999;

            let newVal = currentVal;

            if (button.classList.contains('quantity-minus')) {
                if (currentVal > min) {
                    newVal = currentVal - 1;
                }
            } else if (button.classList.contains('quantity-plus')) {
                if (max === -1 || currentVal < max) {
                    newVal = currentVal + 1;
                }
            }

            if (newVal !== currentVal) {
                input.value = newVal;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        /**
         * Handle quantity input change with debounce
         */
        handleQuantityChange(e) {
            const input = e.target;

            // Clear existing timeout
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
            }

            // Debounce the update (wait 600ms after last change)
            this.updateTimeout = setTimeout(() => {
                this.updateCartItem(input.dataset.key, input.value);
            }, 600);
        }

        /**
         * Handle remove item
         */
        handleRemoveItem(e) {
            e.preventDefault();

            const link = e.target.closest('.remove-item');
            if (!link) return;

            const cartItemKey = link.dataset.cart_item_key;

            if (!cartItemKey) {
                window.location.href = link.getAttribute('href');
                return;
            }

            this.removeItem(cartItemKey);
        }

        /**
         * Handle update cart button
         */
        handleUpdateCart(e) {
            e.preventDefault();
            if (this.form) {
                this.form.submit();
            }
        }

        /**
         * Update cart item quantity via Store API - NO PAGE REFRESH
         */
        async updateCartItem(cartItemKey, quantity) {
            if (this.isUpdating) return;

            this.isUpdating = true;
            this.element.classList.add('loading');

            // Find the item row for optimistic UI
            const itemRow = this.element.querySelector(`[data-key="${cartItemKey}"]`);
            if (itemRow) {
                itemRow.style.opacity = '0.6';
            }

            try {
                const cart = await storeAPI.updateItem(cartItemKey, quantity);

                // Validate that we got a proper response
                if (!cart || typeof cart !== 'object') {
                    throw new Error('Invalid response from server');
                }

                // Update the UI with new cart data
                this.updateCartUI(cart);

                // Sync to shared localStorage for mini-cart cross-navigation cache
                syncCartToLocalStorage(cart);

                // Trigger WooCommerce events for other widgets (mini-cart, etc.)
                document.body.dispatchEvent(new CustomEvent('wc_fragment_refresh'));
                document.body.dispatchEvent(new CustomEvent('storedash_cart_updated', {
                    detail: { cart: cart }
                }));

                // Restore opacity after successful update
                if (itemRow) {
                    itemRow.style.opacity = '1';
                }

            } catch (error) {
                console.error('Cart update error:', error);
                // Restore item opacity
                if (itemRow) {
                    itemRow.style.opacity = '1';
                }
                this.showError(config.i18n.updateError || 'Error updating cart. Please refresh the page.');
            } finally {
                this.element.classList.remove('loading');
                this.isUpdating = false;
            }
        }

        /**
         * Remove item via Store API - NO PAGE REFRESH
         */
        async removeItem(cartItemKey) {
            if (this.isUpdating) return;

            this.isUpdating = true;
            this.element.classList.add('loading');

            // Find the item for animation
            const itemRow = this.element.querySelector(`[data-key="${cartItemKey}"]`);
            if (itemRow) {
                itemRow.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
                itemRow.style.opacity = '0.3';
                itemRow.style.transform = 'translateX(-20px)';
            }

            try {
                const cart = await storeAPI.removeItem(cartItemKey);

                // Animate item removal
                if (itemRow) {
                    itemRow.style.height = itemRow.offsetHeight + 'px';
                    itemRow.style.overflow = 'hidden';

                    requestAnimationFrame(() => {
                        itemRow.style.transition = 'all 0.3s ease';
                        itemRow.style.height = '0';
                        itemRow.style.padding = '0';
                        itemRow.style.margin = '0';
                        itemRow.style.opacity = '0';
                    });

                    // Remove from DOM after animation
                    setTimeout(() => {
                        itemRow.remove();
                        // Update totals and check if cart is empty
                        this.updateCartUI(cart);
                    }, 350);
                } else {
                    this.updateCartUI(cart);
                }

                // Sync to shared localStorage for mini-cart cross-navigation cache
                syncCartToLocalStorage(cart);

                // Trigger WooCommerce events
                document.body.dispatchEvent(new CustomEvent('removed_from_cart', {
                    detail: { cart: cart }
                }));
                document.body.dispatchEvent(new CustomEvent('wc_fragment_refresh'));

            } catch (error) {
                // Restore item on error
                if (itemRow) {
                    itemRow.style.opacity = '1';
                    itemRow.style.transform = 'none';
                }
                this.showError(config.i18n.removeError || 'Error removing item. Please try again.');
            } finally {
                this.element.classList.remove('loading');
                this.isUpdating = false;
            }
        }

        /**
         * Update cart UI from Store API response - NO PAGE REFRESH
         */
        updateCartUI(cart) {
            if (!cart) return;

            const items = cart.items || [];
            const totals = cart.totals || {};

            // Check if cart is empty
            if (items.length === 0) {
                this.renderEmptyCart();
                return;
            }

            // Update each item's price and quantity
            items.forEach(item => {
                try {
                    const itemRow = this.element.querySelector(`[data-key="${item.key}"]`);
                    if (!itemRow) return;

                    // Update quantity input
                    const qtyInput = itemRow.querySelector('input.qty');
                    if (qtyInput && parseInt(qtyInput.value) !== item.quantity) {
                        qtyInput.value = item.quantity;
                    }

                    // Update quantity label if exists
                    const qtyLabel = itemRow.querySelector('.item-quantity-label');
                    if (qtyLabel) {
                        qtyLabel.textContent = `Qty: ${item.quantity}`;
                    }

                    // Update line item price - use item's own totals for currency info
                    const priceEl = itemRow.querySelector('.item-price');
                    if (priceEl && item.totals) {
                        priceEl.textContent = this.formatPrice(item.totals.line_total, item.totals);
                    }

                    // Restore opacity
                    itemRow.style.opacity = '1';
                } catch (err) {
                    console.error('Error updating item:', item.key, err);
                }
            });

            // Update cart totals
            this.updateCartTotals(cart);
        }

        /**
         * Update cart totals section
         */
        updateCartTotals(cart) {
            try {
                const totals = cart.totals || {};
                const totalsCard = this.element.querySelector('.cart-totals-card');
                if (!totalsCard) return;

                // Get all totals rows
                const totalsRows = totalsCard.querySelectorAll('.totals-row');

                totalsRows.forEach(row => {
                    const valueEl = row.querySelector('.row-value');
                    if (!valueEl) return;

                    if (row.classList.contains('total')) {
                        // Update grand total
                        valueEl.textContent = this.formatPrice(totals.total_price, totals);
                    } else if (row.classList.contains('discount')) {
                        // Skip discount rows - they need special handling
                    } else {
                        // Update subtotal (first non-discount, non-total row)
                        valueEl.textContent = this.formatPrice(totals.total_items, totals);
                    }
                });

                // Update shipping if displayed
                if (totals.total_shipping && totals.total_shipping !== '0') {
                    const shippingRow = Array.from(totalsRows).find(row => {
                        const label = row.querySelector('.row-label');
                        return label && (label.textContent.includes('Sending') || label.textContent.includes('Shipping'));
                    });
                    if (shippingRow) {
                        const valueEl = shippingRow.querySelector('.row-value');
                        if (valueEl) {
                            valueEl.textContent = this.formatPrice(totals.total_shipping, totals);
                        }
                    }
                }
            } catch (err) {
                console.error('Error updating cart totals:', err);
            }
        }

        /**
         * Format price from Store API (prices are in minor units)
         * Uses textContent instead of innerHTML to preserve element styling
         */
        formatPrice(price, totals) {
            if (!price) return '';

            const minorUnit = totals.currency_minor_unit !== undefined ? totals.currency_minor_unit : 2;
            const decimal = totals.currency_decimal_separator || '.';
            const thousand = totals.currency_thousand_separator || '.';
            const prefix = totals.currency_prefix || '';
            const suffix = totals.currency_suffix || totals.currency_symbol || '';

            // Handle currencies with no decimals (ISK, JPY, etc.)
            let amount;
            if (minorUnit === 0) {
                // No decimals - price is already correct (ISK, JPY, etc.)
                amount = parseFloat(price);
            } else {
                // Has decimals - divide by 10^minor_unit
                const divisor = Math.pow(10, minorUnit);
                amount = parseFloat(price) / divisor;
            }

            // Format with proper decimals
            const formatted = amount.toFixed(minorUnit);
            const parts = formatted.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousand);

            // Return plain text - matches mini-cart approach
            // The container element (.row-value, .item-price) handles styling
            return `${prefix}${parts.join(decimal)} ${suffix}`.trim();
        }

        /**
         * Render empty cart state
         */
        renderEmptyCart() {
            const emptyMessage = config.i18n.emptyCart || 'Karfan þín er tóm';
            const emptyDescription = config.i18n.emptyCartDescription || '';

            this.element.innerHTML = `
                <div class="storedash-cart-empty">
                    <svg class="empty-cart-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M9 2L6 9H18L15 2M3 9H21L19 20H5L3 9Z"/>
                    </svg>
                    <h2>${emptyMessage}</h2>
                    ${emptyDescription ? `<p>${emptyDescription}</p>` : ''}
                </div>
            `;
        }

        /**
         * Show error message
         */
        showError(message) {
            const existingError = document.querySelector('.storedash-cart-error');
            if (existingError) existingError.remove();

            const errorDiv = document.createElement('div');
            errorDiv.className = 'storedash-cart-error';
            errorDiv.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background: #fee2e2;
                color: #991b1b;
                padding: 12px 16px;
                border-radius: 6px;
                border-left: 4px solid #dc2626;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                z-index: 999999;
                max-width: 300px;
                font-size: 14px;
            `;
            errorDiv.textContent = message;

            document.body.appendChild(errorDiv);

            setTimeout(() => {
                errorDiv.style.opacity = '0';
                errorDiv.style.transition = 'opacity 0.3s';
                setTimeout(() => errorDiv.remove(), 300);
            }, 5000);
        }

        destroy() {
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
            }
        }
    }

    /**
     * Initialize cart widgets
     */
    function initCartWidgets() {
        document.querySelectorAll('.storedash-cart-widget').forEach(element => {
            if (!element.dataset.cartWidgetInitialized) {
                new StoredashCartWidget(element);
                element.dataset.cartWidgetInitialized = 'true';
            }
        });
    }

    /**
     * Initialize on Elementor frontend
     */
    if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {
        elementorFrontend.hooks.addAction('frontend/element_ready/storedash-cart.default', function($scope) {
            const element = $scope[0] || $scope;
            const widget = element.querySelector('.storedash-cart-widget');
            if (widget && !widget.dataset.cartWidgetInitialized) {
                new StoredashCartWidget(widget);
                widget.dataset.cartWidgetInitialized = 'true';
            }
        });
    }

    /**
     * Initialize on DOM ready
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCartWidgets);
    } else {
        initCartWidgets();
    }

    // Export for global access
    window.StoredashCartWidget = StoredashCartWidget;

})();
